import { ProductOverviewAttribute, ProductOverviewCard } from '@app/components/products/ProductOverviewCard';
import { ProductQuantityPreview } from '@app/components/products/ProductQuantityPreview';
import { ProductThumbnail } from '@app/components/products/ProductThumbnail';
import { Notification } from '@app/components/various/Notification';
import { getAllDeliveryWindows } from '@app/ducks';
import { findProductByCentraVariantId } from '@app/logic/products';
import { useTable } from '@app/utils/hooks/table/useTable';
import { isDefined } from '@app/utils/is';
import { createColumnHelper, getCoreRowModel } from '@tanstack/react-table';
import { FailedProducts, Product } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CheckoutFailureTable } from '../CheckoutFailureTable';

interface TableRow extends FailedProducts.ItemsWrongMultiple {
  product: Product.Full;
}

const columnHelper = createColumnHelper<TableRow>();

interface Props {
  variants: FailedProducts.ItemsWrongMultiple[];
  products: Product.Full[];
}

export const WrongMultipleQuantityFailure = ({ products, variants }: Props) => {
  const { t } = useTranslation(['checkoutMessages', 'checkout', 'common', 'products']);

  const deliveryWindows = useSelector(getAllDeliveryWindows);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('productId', {
        cell: info => {
          const data = info.row.original;

          return <ProductThumbnail product={data.product} />;
        },
        header: t('products:product_other'),
        meta: {
          width: '50%',
        },
      }),
      columnHelper.accessor('variantId', {
        cell: info => {
          const data = info.row.original;

          return <div className="body-2">{data.product.variantName}</div>;
        },
        header: t('products:variant_name'),
        meta: {
          width: '24%',
        },
      }),
      columnHelper.accessor('deliveryWindow', {
        cell: info => {
          const deliveryWindow = deliveryWindows.find(delwin => delwin.deliveryWindow === info.getValue().toString());

          return <div className="body-2">{deliveryWindow?.name}</div>;
        },
        header: t('common:delivery_window_one'),
        meta: {
          width: '24%',
        },
      }),
      columnHelper.display({
        cell: info => {
          const { items, product } = info.row.original;

          return (
            <ProductQuantityPreview
              title={t('checkout:original_quantity')}
              items={items.map(item => item.original)}
              itemTable={product.itemTable}
            />
          );
        },
        header: t('checkout:original_quantity'),
        meta: {
          width: '1%',
        },
      }),
      columnHelper.display({
        cell: info => {
          const { items, product } = info.row.original;

          return (
            <div className="flex items-center gap-5">
              <ProductQuantityPreview
                // eslint-disable-next-line sonarjs/no-duplicate-string
                title={t('checkout:allowed_quantity')}
                items={items.map(item => item.correctQuantityLower)}
                itemTable={product.itemTable}
              />
              <span>{t('common:or')}</span>
              <ProductQuantityPreview
                title={t('checkout:allowed_quantity')}
                items={items.map(item => item.correctQuantityHigher)}
                itemTable={product.itemTable}
              />
            </div>
          );
        },
        header: t('checkout:allowed_quantity'),
        meta: {
          width: '1%',
        },
      }),
    ],
    [deliveryWindows, t],
  );

  const data = React.useMemo(() => {
    return variants
      .map(failedProduct => ({
        ...failedProduct,
        product: findProductByCentraVariantId(products, failedProduct.variantId),
      }))
      .filter(variant => isDefined(variant.product)) as TableRow[];
  }, [products, variants]);

  const table = useTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex w-full flex-col gap-5">
      <Notification type="error" message={t('checkoutMessages:wrong_multiple_error.header')} />
      <p className="body-2">{t('checkoutMessages:wrong_multiple_error.details')}</p>
      <CheckoutFailureTable
        table={table}
        title={t('products:product_other')}
        renderCard={({ original: { product, deliveryWindow, items } }) => (
          <ProductOverviewCard product={product} variantName={product.variantName} deliveryWindowId={deliveryWindow.toString()}>
            <ProductOverviewAttribute
              label={t('checkout:original_quantity')}
              value={
                <ProductQuantityPreview
                  title={t('checkout:original_quantity')}
                  items={items.map(item => item.original)}
                  itemTable={product.itemTable}
                />
              }
            />
            <ProductOverviewAttribute
              label={t('checkout:allowed_quantity')}
              value={
                <div className="flex items-center gap-5">
                  <ProductQuantityPreview
                    title={t('checkout:allowed_quantity')}
                    items={items.map(item => item.correctQuantityLower)}
                    itemTable={product.itemTable}
                  />
                  <span>{t('common:or')}</span>
                  <ProductQuantityPreview
                    title={t('checkout:allowed_quantity')}
                    items={items.map(item => item.correctQuantityHigher)}
                    itemTable={product.itemTable}
                  />
                </div>
              }
            />
          </ProductOverviewCard>
        )}
      />
    </div>
  );
};
